import { useCallback, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import {
  BookOpen,
  CheckCircle2,
  Home,
  Loader2,
  Plus,
  RotateCcw,
  Sparkles,
  Volume2,
} from 'lucide-react'
import { articlesApi } from '@/api/articles'
import { understandApi } from '@/api/understand'
import { vocabularyApi } from '@/api/vocabulary'
import { Button } from '@/components/ui/Button'
import { CefrBadge } from '@/components/CefrBadge'
import { extractSentenceContext } from '@/components/reading/extractSentenceContext'
import { cn } from '@/components/ui/cn'
import { formatMessage } from '@/i18n/format'
import { useMinCopy } from '@/i18n/useUiLocale'
import {
  LEARNING_DEMO_ARTICLE_ID,
  LEARNING_DEMO_PARAGRAPH,
  LEARNING_DEMO_TARGET_WORD,
} from '@/lib/learningDemoFixtures'
import { playPronunciation } from '@/lib/playPronunciation'
import { tokenizeContent } from '@/lib/tokenizeContent'
import type { UnderstandResponse } from '@/types/content'
import type { UserWordDetail } from '@/types/user'

type DemoPhase =
  | 'intro'
  | 'read'
  | 'understand'
  | 'vocabulary'
  | 'refresh'
  | 'today'
  | 'complete'

const QUEST_PHASES: DemoPhase[] = ['read', 'understand', 'vocabulary', 'refresh', 'today']

const DEMO_CARD_SHELL =
  'rounded-xl border border-primary/20 bg-gradient-to-br from-primary/5 to-card p-5 sm:p-6'

const NATIVE_LANGUAGE_NAMES: Record<string, string> = {
  ru: 'Russian',
  pl: 'Polish',
  de: 'German',
  fr: 'French',
  es: 'Spanish',
  uk: 'Ukrainian',
}

function getPhaseCopy(demo: ReturnType<typeof useMinCopy>['learningDemo']): Record<
  Exclude<DemoPhase, 'intro' | 'complete'>,
  { icon: typeof BookOpen; title: string; body: string }
> {
  return {
    read: { icon: BookOpen, title: demo.readTitle, body: demo.readBody },
    understand: {
      icon: Sparkles,
      title: demo.understandTitle,
      body: demo.understandBody,
    },
    vocabulary: {
      icon: BookOpen,
      title: demo.vocabularyTitle,
      body: demo.vocabularyBody,
    },
    refresh: {
      icon: RotateCcw,
      title: demo.refreshTitle,
      body: demo.refreshBody,
    },
    today: { icon: Home, title: demo.todayTitle, body: demo.todayBody },
  }
}

function questIndex(phase: DemoPhase): number {
  const idx = QUEST_PHASES.indexOf(phase)
  return idx >= 0 ? idx + 1 : 0
}

interface WordLookup {
  selection: string
  context: string
}

interface LearningDemoPanelProps {
  understand: UnderstandResponse | undefined
  contextSentence?: string
  loading: boolean
  onSave: () => void
  saving: boolean
  saved: boolean
  canSave: boolean
}

function highlightWordInContext(sentence: string, word: string) {
  const pattern = new RegExp(`(${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'i')
  const parts = sentence.split(pattern)

  return parts.map((part, index) =>
    part.toLowerCase() === word.toLowerCase() ? (
      <mark
        key={index}
        className="rounded-sm bg-primary/15 px-0.5 font-medium text-primary underline decoration-primary/60 decoration-2 underline-offset-2"
      >
        {part}
      </mark>
    ) : (
      <span key={index}>{part}</span>
    ),
  )
}

function LearningDemoPanel({
  understand,
  contextSentence,
  loading,
  onSave,
  saving,
  saved,
  canSave,
}: LearningDemoPanelProps) {
  const demo = useMinCopy().learningDemo
  const popup = understand?.wordDetails
  const primarySense =
    popup?.localizedDefinitions.find((d) => d.isPrimary) ?? popup?.localizedDefinitions[0]
  const nativeLabel = popup?.nativeLanguage
    ? NATIVE_LANGUAGE_NAMES[popup.nativeLanguage] ?? popup.nativeLanguage.toUpperCase()
    : demo.translation
  const definition =
    primarySense?.englishGloss ??
    popup?.definitions.find((d) => d.isPrimary)?.definition ??
    popup?.definitions[0]?.definition

  if (loading) {
    return (
      <div className="flex h-full min-h-[220px] flex-col items-center justify-center lg:min-h-[280px]">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
        <p className="mt-3 text-sm text-muted-foreground">{demo.loadingExplanation}</p>
      </div>
    )
  }

  if (!understand || !popup || !primarySense) return null

  return (
    <div
      className="flex h-full flex-col animate-in fade-in slide-in-from-bottom-2 duration-300"
      data-testid="learning-demo-panel"
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xl font-semibold text-foreground">{understand.selection}</span>
        {popup.phonetic ? (
          <span className="text-xs text-muted-foreground">{popup.phonetic}</span>
        ) : null}
        {popup.cefrLevel ? <CefrBadge level={popup.cefrLevel} /> : null}
        {primarySense.partOfSpeech ? (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs italic text-muted-foreground">
            {primarySense.partOfSpeech}
          </span>
        ) : null}
      </div>

      <div className="mt-4 rounded-lg bg-green-50 px-3 py-2 dark:bg-green-900/20" data-testid="popup-translation">
        <p className="mb-0.5 text-xs font-medium uppercase tracking-wide text-green-700/80 dark:text-green-400/80">
          {nativeLabel}
        </p>
        <p className="text-sm font-semibold text-green-800 dark:text-green-300">
          {primarySense.translation}
        </p>
      </div>

      {definition ? (
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{definition}</p>
      ) : null}

      {contextSentence ? (
        <div className="mt-4 rounded-lg border border-border/60 bg-muted/20 px-3 py-2.5">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {demo.inContext}
          </p>
          <p className="mt-1.5 text-sm leading-relaxed text-foreground">
            {highlightWordInContext(contextSentence, understand.selection)}
          </p>
        </div>
      ) : null}

      <div className="mt-auto pt-4">
        {saved ? (
          <div className="flex items-center justify-center gap-2 rounded-md bg-green-500/10 px-4 py-3 text-sm font-medium text-green-700 dark:text-green-400">
            <CheckCircle2 className="h-4 w-4" />
            {demo.saved}
          </div>
        ) : (
          <Button
            className="w-full"
            onClick={onSave}
            loading={saving}
            disabled={!canSave}
            data-testid="learning-demo-save"
          >
            <Plus className="mr-1.5 h-3.5 w-3.5" />
            {saving ? demo.saving : demo.saveWord}
          </Button>
        )}
      </div>
    </div>
  )
}

interface LearningDemoReaderProps {
  title: string
  content: string
  phase: DemoPhase
  understand: UnderstandResponse | undefined
  contextSentence?: string
  understandLoading: boolean
  onWordTap: (lookup: WordLookup) => void
  onSave: () => void
  saving: boolean
  saved: boolean
  canSave: boolean
}

function LearningDemoReader({
  title,
  content,
  phase,
  understand,
  contextSentence,
  understandLoading,
  onWordTap,
  onSave,
  saving,
  saved,
  canSave,
}: LearningDemoReaderProps) {
  const demo = useMinCopy().learningDemo
  const [hasTapped, setHasTapped] = useState(false)
  const tokens = useMemo(() => tokenizeContent(content), [content])

  const handleWordClick = useCallback(() => {
    if (phase !== 'read' || hasTapped) return
    setHasTapped(true)
    onWordTap({
      selection: LEARNING_DEMO_TARGET_WORD,
      context: extractSentenceContext(content, LEARNING_DEMO_TARGET_WORD),
    })
  }, [phase, hasTapped, onWordTap, content])

  const showPanel = phase === 'understand' || (phase === 'read' && hasTapped)

  return (
    <div
      className="overflow-hidden rounded-2xl border border-border/80 bg-card shadow-lg"
      data-testid="learning-demo-reader"
    >
      <div className="border-b border-border/60 bg-muted/15 px-5 py-3 sm:px-6">
        <p className="text-sm font-medium text-foreground">{title}</p>
      </div>

      <div className="grid border-b border-border/60 bg-muted/15 px-5 py-2.5 text-xs font-medium uppercase tracking-wide text-muted-foreground sm:px-6 lg:grid-cols-2">
        <span>{demo.readLabel}</span>
        <span className="mt-1 lg:mt-0">{demo.understandLabel}</span>
      </div>

      <div className="grid lg:grid-cols-2">
        <div className="border-b border-border/60 p-6 sm:p-7 lg:border-b-0 lg:border-r">
          <p className="text-[1.05rem] leading-[1.75] text-foreground sm:text-lg sm:leading-[1.8]">
            {tokens.map((token, i) => {
              if (!token.isWord) {
                return <span key={i}>{token.text}</span>
              }

              const isTarget = token.lower === LEARNING_DEMO_TARGET_WORD
              const isInteractive = isTarget && phase === 'read' && !hasTapped

              return (
                <span
                  key={i}
                  data-demo-word={isTarget ? token.lower : undefined}
                  onClick={isInteractive ? handleWordClick : undefined}
                  data-word-clickable={isInteractive ? 'true' : undefined}
                  className={cn(
                    isTarget &&
                      'font-medium text-primary underline decoration-primary/60 decoration-2 underline-offset-[4px]',
                    isInteractive &&
                      'cursor-pointer rounded px-0.5 transition-all hover:bg-primary/15 animate-pulse ring-2 ring-primary/50 ring-offset-2 ring-offset-card',
                    isTarget && hasTapped && 'bg-primary/15 decoration-primary decoration-solid',
                  )}
                >
                  {token.text}
                </span>
              )
            })}
          </p>
          {phase === 'read' && !hasTapped ? (
            <p className="mt-4 text-sm text-muted-foreground">{demo.tapWordHint}</p>
          ) : null}
        </div>

        <div className="bg-muted/10 p-6 sm:p-7">
          {showPanel ? (
            <LearningDemoPanel
              understand={understand}
              contextSentence={contextSentence}
              loading={understandLoading && !understand}
              onSave={onSave}
              saving={saving}
              saved={saved}
              canSave={canSave}
            />
          ) : (
            <div className="flex h-full min-h-[220px] flex-col items-center justify-center rounded-xl border border-dashed border-border/70 bg-muted/20 px-4 py-8 text-center lg:min-h-[280px]">
              <Sparkles className="mb-2 h-5 w-5 text-primary" aria-hidden />
              <p className="text-sm font-medium text-foreground">{demo.tapWordHint}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function SavedWordCard({
  word,
  understand,
}: {
  word: UserWordDetail
  understand: UnderstandResponse | undefined
}) {
  const demo = useMinCopy().learningDemo
  const popup = understand?.wordDetails
  const primarySense =
    popup?.localizedDefinitions.find((d) => d.isPrimary) ?? popup?.localizedDefinitions[0]
  const nativeLabel = popup?.nativeLanguage
    ? NATIVE_LANGUAGE_NAMES[popup.nativeLanguage] ?? popup.nativeLanguage.toUpperCase()
    : demo.translation
  const phonetic = word.phonetic ?? popup?.phonetic
  const audioUrl = word.audioUrl ?? popup?.audioUrl
  const partOfSpeech = word.partOfSpeech ?? primarySense?.partOfSpeech
  const definition =
    word.shortExplanation ??
    primarySense?.englishGloss ??
    popup?.definitions.find((d) => d.isPrimary)?.definition
  const exampleText =
    word.examples[0]?.english ?? popup?.examples[0] ?? understand?.examples[0]

  return (
    <div className={DEMO_CARD_SHELL} data-testid="learning-demo-saved-word">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {demo.savedWordLabel}
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-2xl font-bold capitalize text-foreground sm:text-3xl">{word.word}</span>
        <button
          type="button"
          onClick={() => playPronunciation(word.word, audioUrl)}
          className="rounded-full p-1.5 text-primary hover:bg-primary/10"
          aria-label={demo.playPronunciation}
          data-testid="learning-demo-play-audio"
        >
          <Volume2 className="h-5 w-5" />
        </button>
        {phonetic ? <span className="text-sm text-muted-foreground">{phonetic}</span> : null}
        {word.cefrLevel ? <CefrBadge level={word.cefrLevel} /> : null}
        {partOfSpeech ? (
          <span className="rounded-full bg-muted px-2 py-0.5 text-xs italic text-muted-foreground">
            {partOfSpeech}
          </span>
        ) : null}
        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
          {demo.vocabularyStatusNew}
        </span>
      </div>

      <div className="mt-4 rounded-lg bg-green-50 px-3 py-2 dark:bg-green-900/20">
        <p className="mb-0.5 text-xs font-medium uppercase tracking-wide text-green-700/80 dark:text-green-400/80">
          {nativeLabel}
        </p>
        <p className="text-sm font-semibold text-green-800 dark:text-green-300">{word.translation}</p>
      </div>

      {definition ? (
        <div className="mt-4">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {demo.definitionLabel}
          </p>
          <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{definition}</p>
        </div>
      ) : null}

      {exampleText ? (
        <div className="mt-3">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {demo.exampleLabel}
          </p>
          <p className="mt-1 text-sm italic leading-relaxed text-muted-foreground">
            &ldquo;{exampleText}&rdquo;
          </p>
        </div>
      ) : null}

      <p className="mt-4 text-xs leading-relaxed text-muted-foreground/90">
        {demo.vocabularyMoreHint}
      </p>
    </div>
  )
}

interface DemoWordPreview {
  word: string
  translation: string
  phonetic?: string
  partOfSpeech?: string
  senseGloss?: string
  cefrLevel?: UserWordDetail['cefrLevel']
}

function DemoFlashCardPreview({ preview }: { preview: DemoWordPreview }) {
  const demo = useMinCopy().learningDemo
  const [isFlipped, setIsFlipped] = useState(false)

  return (
    <div className="space-y-4" data-testid="learning-demo-flashcard">
      <div className="mx-auto w-full max-w-sm sm:max-w-md" style={{ perspective: '1200px' }}>
        <div
          role="button"
          tabIndex={0}
          className="relative h-64 w-full cursor-pointer sm:h-72"
          onClick={() => setIsFlipped((value) => !value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              setIsFlipped((value) => !value)
            }
          }}
          aria-label={demo.refreshFlipHint}
          style={{
            transformStyle: 'preserve-3d',
            transform: isFlipped ? 'rotateY(180deg)' : 'rotateY(0deg)',
            transition: 'transform 500ms ease',
          }}
        >
          <div
            className="absolute inset-0 flex items-center justify-center rounded-xl border border-border/80 bg-card p-6 text-center shadow-sm sm:p-8"
            style={{ backfaceVisibility: 'hidden' }}
          >
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                Prompt
              </p>
              <h2 className="mt-4 text-3xl font-bold capitalize text-foreground sm:text-4xl">
                {preview.word}
              </h2>
              {preview.phonetic ? (
                <p className="mt-2 text-sm text-muted-foreground">{preview.phonetic}</p>
              ) : null}
              {preview.partOfSpeech ? (
                <p className="mt-3 text-sm italic text-muted-foreground">{preview.partOfSpeech}</p>
              ) : null}
            </div>
          </div>

          <div
            className="absolute inset-0 flex items-center justify-center rounded-xl border border-border/80 bg-card p-6 text-center shadow-sm sm:p-8"
            style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
          >
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                Answer
              </p>
              <p className="text-2xl font-bold text-foreground sm:text-3xl">{preview.translation}</p>
              {preview.senseGloss ? (
                <p className="text-sm leading-relaxed text-muted-foreground">{preview.senseGloss}</p>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <p className="text-center text-xs text-muted-foreground">{demo.refreshFlipHint}</p>
      <p className="text-center text-xs leading-relaxed text-muted-foreground/90 sm:px-4">
        {demo.refreshMoreHint}
      </p>
    </div>
  )
}

function TodayPlanPreview() {
  const demo = useMinCopy().learningDemo
  const steps = [
    {
      step: 1,
      label: demo.todayStepReading,
      hint: demo.todayStepReadingHint,
      color: 'bg-amber-500',
      progress: 'w-[70%]',
    },
    {
      step: 2,
      label: demo.todayStepNewWords,
      hint: demo.todayStepNewWordsHint,
      color: 'bg-purple-500',
      progress: 'w-[40%]',
    },
    {
      step: 3,
      label: demo.todayStepRefresh,
      hint: demo.todayStepRefreshHint,
      color: 'bg-blue-500',
      progress: 'w-[15%]',
    },
  ]

  return (
    <div className={DEMO_CARD_SHELL} data-testid="learning-demo-today-preview">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {demo.todayCardLabel}
      </p>
      <p className="mt-2 text-sm text-muted-foreground">{demo.todayPreviewHint}</p>

      <div className="mt-4 space-y-3">
        {steps.map(({ step, label, hint, color, progress }) => (
          <div
            key={step}
            className="rounded-lg border border-border/60 bg-card/80 px-4 py-3"
          >
            <div className="flex items-start gap-3">
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                {step}
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground">{label}</p>
                <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{hint}</p>
                <div className="mt-2.5 h-1.5 w-full rounded-full bg-secondary">
                  <div className={`h-1.5 rounded-full ${color} ${progress}`} />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <p className="mt-4 text-xs leading-relaxed text-muted-foreground/90">
        {demo.todayMoreHint}
      </p>
    </div>
  )
}

function ConceptPreview({
  phase,
  wordPreview,
}: {
  phase: 'refresh' | 'today'
  wordPreview?: DemoWordPreview
}) {
  if (phase === 'refresh') {
    return wordPreview ? <DemoFlashCardPreview preview={wordPreview} /> : null
  }

  return <TodayPlanPreview />
}

interface LearningDemoQuestProps {
  onComplete: () => void
  onSkip: () => void
}

export function LearningDemoQuest({ onComplete, onSkip }: LearningDemoQuestProps) {
  const min = useMinCopy()
  const demo = min.learningDemo
  const brand = min.brand
  const [phase, setPhase] = useState<DemoPhase>('intro')
  const [lookup, setLookup] = useState<WordLookup | null>(null)
  const [savedWord, setSavedWord] = useState<UserWordDetail | null>(null)

  const articleQuery = useQuery({
    queryKey: ['learning-demo-article', LEARNING_DEMO_ARTICLE_ID],
    queryFn: () => articlesApi.getArticle(LEARNING_DEMO_ARTICLE_ID).then((r) => r.data),
  })

  const articleContent = articleQuery.data?.content ?? LEARNING_DEMO_PARAGRAPH
  const articleTitle = articleQuery.data?.title ?? demo.demoArticleTitle

  const understandQuery = useQuery({
    queryKey: [
      'understand',
      'learning-demo',
      LEARNING_DEMO_ARTICLE_ID,
      lookup?.selection,
      lookup?.context,
    ],
    queryFn: () =>
      understandApi
        .understand({
          selection: lookup!.selection,
          context: lookup!.context,
          sourceType: 'ARTICLE',
          articleId: LEARNING_DEMO_ARTICLE_ID,
        })
        .then((r) => r.data),
    enabled: Boolean(lookup),
    staleTime: 1000 * 60 * 5,
  })

  const existingWordQuery = useQuery({
    queryKey: ['learning-demo-existing-word', LEARNING_DEMO_TARGET_WORD],
    queryFn: async () => {
      const page = await vocabularyApi.getWords({ search: LEARNING_DEMO_TARGET_WORD, size: 10 })
      const match = page.data.content.find(
        (w) => w.word.toLowerCase() === LEARNING_DEMO_TARGET_WORD,
      )
      if (!match) return null
      const detail = await vocabularyApi.getWordById(match.id)
      return detail.data
    },
    staleTime: 0,
  })

  const existingWord = existingWordQuery.data ?? null

  const saveMutation = useMutation({
    mutationFn: async () => {
      const popup = understandQuery.data?.wordDetails
      const sense =
        popup?.localizedDefinitions.find((d) => d.isPrimary) ?? popup?.localizedDefinitions[0]
      const response = await vocabularyApi.addWord({
        word: LEARNING_DEMO_TARGET_WORD,
        translation: sense?.translation || popup?.primaryTranslation || undefined,
        senseKey: sense?.senseKey,
        contextSentence: lookup?.context,
      })
      return response.data
    },
    onSuccess: (word) => {
      setSavedWord(word)
      setPhase('vocabulary')
    },
  })

  const handleSave = useCallback(() => {
    if (existingWord) {
      setSavedWord(existingWord)
      setPhase('vocabulary')
      return
    }
    saveMutation.mutate()
  }, [existingWord, saveMutation])

  const handleWordTap = useCallback((nextLookup: WordLookup) => {
    setLookup(nextLookup)
    setPhase('understand')
  }, [])

  const advance = useCallback(() => {
    setPhase((current) => {
      if (current === 'intro') return 'read'
      if (current === 'vocabulary') return 'refresh'
      if (current === 'refresh') return 'today'
      if (current === 'today') return 'complete'
      return current
    })
  }, [])

  const questStep = questIndex(phase)
  const phaseInfo = phase !== 'intro' && phase !== 'complete' ? getPhaseCopy(demo)[phase] : null
  const PhaseIcon = phaseInfo?.icon
  const canSave =
    Boolean(understandQuery.data?.wordDetails) &&
    !saveMutation.isPending &&
    !savedWord &&
    !understandQuery.isLoading

  const wordPreview = useMemo<DemoWordPreview | undefined>(() => {
    if (!savedWord) return undefined
    const popup = understandQuery.data?.wordDetails
    const primarySense =
      popup?.localizedDefinitions.find((d) => d.isPrimary) ?? popup?.localizedDefinitions[0]
    return {
      word: savedWord.word,
      translation: savedWord.translation,
      phonetic: savedWord.phonetic ?? popup?.phonetic,
      partOfSpeech: savedWord.partOfSpeech ?? primarySense?.partOfSpeech,
      cefrLevel: savedWord.cefrLevel,
      senseGloss:
        savedWord.shortExplanation ??
        primarySense?.englishGloss ??
        popup?.definitions.find((d) => d.isPrimary)?.definition,
    }
  }, [savedWord, understandQuery.data])

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-slate-100 to-slate-50 dark:from-background dark:to-background">
      <header className="flex items-center justify-between px-4 py-4 sm:px-6">
        <div>
          <p className="text-lg font-bold text-primary">{brand.name}</p>
          <p className="text-xs text-muted-foreground">{brand.tagline}</p>
        </div>
        {phase !== 'complete' ? (
          <Button
            type="button"
            className="h-9 w-auto bg-transparent px-3 text-sm text-muted-foreground shadow-none hover:bg-muted hover:text-foreground"
            onClick={onSkip}
            data-testid="learning-demo-skip"
          >
            {demo.skip}
          </Button>
        ) : null}
      </header>

      <main className="mx-auto flex w-full max-w-3xl flex-1 flex-col px-4 pb-10 pt-2 sm:px-6">
        {phase === 'intro' ? (
          <div
            className="flex flex-1 flex-col items-center justify-center text-center"
            data-testid="learning-demo-intro"
          >
            <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-primary/10">
              <Sparkles className="h-7 w-7 text-primary" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{demo.introTitle}</h1>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-muted-foreground sm:text-lg">
              {demo.introPhilosophy}
            </p>
            <Button className="mt-8 max-w-xs" onClick={advance} data-testid="learning-demo-start">
              {demo.introCta}
            </Button>
          </div>
        ) : null}

        {phase !== 'intro' && phase !== 'complete' ? (
          <div className="mb-6 space-y-4">
            <div className="flex items-center gap-2">
              {QUEST_PHASES.map((step, index) => {
                const done = questIndex(phase) > index + 1
                const active = phase === step
                return (
                  <div
                    key={step}
                    className={cn(
                      'h-1.5 flex-1 rounded-full transition-colors',
                      done ? 'bg-primary' : active ? 'bg-primary/60' : 'bg-muted',
                    )}
                  />
                )
              })}
            </div>

            <div className="flex items-start gap-3">
              {PhaseIcon ? (
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <PhaseIcon className="h-5 w-5 text-primary" />
                </div>
              ) : null}
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-primary">
                  {formatMessage(demo.questStep, { current: questStep, total: QUEST_PHASES.length })}
                </p>
                <h2 className="mt-0.5 text-xl font-semibold tracking-tight">{phaseInfo?.title}</h2>
                <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{phaseInfo?.body}</p>
              </div>
            </div>
          </div>
        ) : null}

        {phase === 'read' || phase === 'understand' ? (
          <LearningDemoReader
            title={articleTitle}
            content={articleContent}
            phase={phase}
            understand={understandQuery.data}
            contextSentence={lookup?.context}
            understandLoading={understandQuery.isLoading}
            onWordTap={handleWordTap}
            onSave={handleSave}
            saving={saveMutation.isPending}
            saved={Boolean(savedWord)}
            canSave={canSave}
          />
        ) : null}

        {phase === 'vocabulary' && savedWord ? (
          <div className="space-y-6">
            <SavedWordCard word={savedWord} understand={understandQuery.data} />
            <Button onClick={advance} data-testid="learning-demo-continue">
              {demo.continue}
            </Button>
          </div>
        ) : null}

        {phase === 'refresh' || phase === 'today' ? (
          <div className="space-y-6">
            <ConceptPreview phase={phase} wordPreview={wordPreview} />
            <Button onClick={advance} data-testid="learning-demo-continue">
              {demo.continue}
            </Button>
          </div>
        ) : null}

        {phase === 'complete' ? (
          <div
            className="flex flex-1 flex-col items-center justify-center text-center"
            data-testid="learning-demo-complete"
          >
            <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-full bg-green-500/15">
              <CheckCircle2 className="h-7 w-7 text-green-600 dark:text-green-400" />
            </div>
            <h1 className="text-2xl font-bold tracking-tight">{demo.completeTitle}</h1>
            <p className="mt-4 max-w-md text-base leading-relaxed text-muted-foreground">
              {demo.completeBody}
            </p>
            <p className="mt-4 max-w-md text-sm leading-relaxed text-muted-foreground/90">
              {demo.completeHelpHint}
            </p>
            <Button className="mt-8 max-w-xs" onClick={onComplete} data-testid="learning-demo-finish">
              {demo.completeCta}
            </Button>
          </div>
        ) : null}

        {saveMutation.isError && !savedWord ? (
          <p className="mt-4 text-center text-sm text-destructive">
            {demo.saveWordFailed}
          </p>
        ) : null}

        {understandQuery.isError ? (
          <p className="mt-4 text-center text-sm text-destructive">
            {demo.loadExplanationFailed}
          </p>
        ) : null}
      </main>
    </div>
  )
}
